import { NestAPI, adImageService } from '@marketing-milk/frontend'
import { AdVideo, CREATIVE_IMPORT_LIMITS } from '@marketing-milk/interfaces'
import { UploadedMedia } from './mediaDraft'

const IMAGE_TYPES = ['.png', '.jpg', '.jpeg']
const VIDEO_TYPES = ['.mp4', '.mov', '.gif']

export const REVIEW_MEDIA_ACCEPT = [...IMAGE_TYPES, ...VIDEO_TYPES].join(',')

const megabytes = (bytes: number) => Math.round(bytes / (1024 * 1024))

/**
 * Uploads one replacement asset and returns it as persisted.
 *
 * Deliberately not `adVideoService.createVideo`: that polls Facebook until the
 * transcode reports 100%, which can take minutes and leaves its promise unsettled
 * if a poll rejects. The review surface needs only the persisted row — it has the
 * Facebook identifier the save writes, and playback uses our own stored copy — so
 * this returns as soon as the POST does.
 */
export async function uploadReviewMedia(businessID: number, file: File): Promise<UploadedMedia> {
  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`

  if (IMAGE_TYPES.includes(extension)) {
    if (file.size > CREATIVE_IMPORT_LIMITS.imageMaxBytes) {
      throw new Error(`Images must be under ${megabytes(CREATIVE_IMPORT_LIMITS.imageMaxBytes)}MB.`)
    }
    return { mediaType: 'image', asset: await adImageService.createImage(businessID, file) }
  }

  if (VIDEO_TYPES.includes(extension)) {
    if (file.size > CREATIVE_IMPORT_LIMITS.videoMaxBytes) {
      throw new Error(`Videos must be under ${megabytes(CREATIVE_IMPORT_LIMITS.videoMaxBytes)}MB.`)
    }
    const body = new FormData()
    body.append('video', file)
    const response = await NestAPI.post<AdVideo>(`businesses/${businessID}/ad-videos`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return { mediaType: 'video', asset: response.data }
  }

  throw new Error(`${extension} files aren't supported — use ${REVIEW_MEDIA_ACCEPT}.`)
}
